import { useCallback, useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

import { activeViewerIdAtom, viewerImagesAtom } from "../../atom";
import { getLoginUser } from "../../api/user";
import { useAnnotationInfo } from "../../hooks/useAnnotationInfo";
import { AnnotationInfoPanel as AnnotationInfoPanelComponent } from "./AnnotationInfoPanel";
import {
  StMainWrapper,
  StAnnotationBox,
  PanelHeader,
  PanelTitle,
  CloseButton,
  PanelContent,
} from "./SideMenuSample2";

interface Props {
  activePanel: string | null;
  onClose: () => void;
}

// 뷰어 페이지 사이드 탭 — Image / Gross 용/어노테이션 탭만 제공
const ImageSideMenu = ({ activePanel, onClose }: Props) => {
  const activeViewerId = useRecoilValue(activeViewerIdAtom);
  const [viewerImages, setViewerImages] = useRecoilState(viewerImagesAtom);
  const image = viewerImages[activeViewerId];
  const isGross = image?.kind === "gross";

  const [currentUser, setCurrentUser] = useState({ username: "", role: "" });

  useEffect(() => {
    getLoginUser()
      .then((u) => setCurrentUser({ username: u?.name ?? "", role: u?.role ?? "" }))
      .catch(() => {});
  }, []);

  const setAnnotationsFromHook = useCallback(
    (next: any) => {
      setViewerImages((prev) => {
        const target = prev[activeViewerId];
        if (!target) return prev;
        const list = Array.isArray(next?.annotations) ? next.annotations : [];
        return { ...prev, [activeViewerId]: { ...target, annotations: list } };
      });
    },
    [activeViewerId, setViewerImages]
  );

  const annotationInfo = useAnnotationInfo({
    currentSlide: image ? { annotations: image.annotations ?? [] } : null,
    setCurrentSlide: setAnnotationsFromHook,
    isImageMode: image?.kind === "image",
    isGrossMode: isGross,
    photoId: image?.kind === "image" ? image.id : undefined,
    grossPhotoId: isGross ? image?.id : undefined,
  });

  const annotationIdOf = (anno: any) =>
    isGross ? anno.antnGrossPhotoId || anno.annoId : anno.antnPhotoId || anno.annoId;

  const handleEditAnnotation = (annotation: any) => {
    if (annotation.writer !== currentUser.username && currentUser.role !== "ADMIN_MAIN") {
      window.alert("수정 권한이 없습니다.");
      return;
    }
    annotationInfo.setSelectedAnnotation(annotation);
    setTimeout(() => annotationInfo.setShowEditModal(true), 0);
  };

  const DisplayColor = ({ annotation }: { annotation: any }) => {
    const annoInfo = annotation?.annoInfo;
    const color =
      annoInfo && annoInfo[0] === "g" && annoInfo[1]?.class === "annotation arrow-group"
        ? annoInfo[1]?.stroke || "#285F3D"
        : annoInfo?.[1]?.stroke;
    return (
      <div
        style={{
          width: "1rem",
          height: "1rem",
          backgroundColor: color,
          borderRadius: "3px",
          marginLeft: "3px",
        }}
      />
    );
  };

  return (
    <StMainWrapper>
      {activePanel === "annotation" && (
        <>
          <PanelHeader>
            <PanelTitle>ANNOTATIONS</PanelTitle>
            <CloseButton onClick={onClose}>×</CloseButton>
          </PanelHeader>
          <PanelContent>
            {(image?.annotations || []).map((annotation: any, idx: number) => (
              <StAnnotationBox key={`${idx}_${annotationIdOf(annotation)}`}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.8rem" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.3rem" }}>
                      <DisplayColor annotation={annotation} />
                      <div
                        title={`${annotation.content}`}
                        style={{
                          fontSize: "1rem",
                          fontWeight: "600",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "10rem",
                          color: "#fff",
                        }}
                      >
                        {annotation.content}
                      </div>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          color: "#1890ff",
                          padding: "2px 6px",
                          backgroundColor: "#051e34",
                          border: "1px solid #1e3a52",
                          borderRadius: "3px",
                          flexShrink: 0,
                        }}
                      >
                        {annotationInfo.getShapeType(annotation)}
                      </div>
                    </div>
                    <div style={{ fontSize: "0.85rem", color: "#8899aa", marginLeft: "1.5rem" }}>
                      {annotation.writer}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexShrink: 0 }}>
                    <EditOutlined
                      onClick={() => handleEditAnnotation(annotation)}
                      style={{ fontSize: "1.1rem", cursor: "pointer", color: "#8899aa" }}
                    />
                    <DeleteOutlined
                      onClick={() => annotationInfo.handleDeleteAnnotation(annotation)}
                      style={{ fontSize: "1.1rem", cursor: "pointer", color: "#8899aa" }}
                    />
                  </div>
                </div>
              </StAnnotationBox>
            ))}
          </PanelContent>
        </>
      )}

      {/* 어노테이션 수정 모달 */}
      <AnnotationInfoPanelComponent {...annotationInfo} />
    </StMainWrapper>
  );
};

export default ImageSideMenu;
